import { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import vizitProBanner from '@/assets/brand/banner.png';
import { useUser } from '@/components/auth-provider';
import { useLanguage } from '@/contexts/language-context';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { createBusiness, setBusinessTier } from '@/lib/api/businesses';
import { updateMe } from '@/lib/api/users';

import type { Step, OnboardingData } from './types';
import { StepIndicator } from './step-indicator';
import { StepBusiness } from './step-business';
import { StepProfile } from './step-profile';
import { StepTier } from './step-tier';

export function Onboarding() {
  const { t } = useLanguage();
  const { completeOnboarding, user } = useUser();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(0);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState<Partial<OnboardingData>>({});

  const stepLabels = [t.stepBusiness, t.stepProfile, t.stepPlan];

  const titles = [
    { title: t.onboardingTitle, subtitle: t.onboardingSubtitle },
    { title: t.profileTitle, subtitle: t.profileSubtitle },
    { title: t.tierTitle, subtitle: t.tierSubtitle },
  ];

  function handleBusiness(values: Pick<OnboardingData, 'language' | 'businessName' | 'businessTypeId'>) {
    setData((prev) => ({ ...prev, ...values }));
    setStep(1);
  }

  function handleProfile(values: Pick<OnboardingData, 'fullName' | 'address' | 'phone'>) {
    setData((prev) => ({ ...prev, ...values }));
    setStep(2);
  }

  async function handleTier(tierId: string) {
    const final: OnboardingData = {
      language: data.language ?? 'en',
      businessName: data.businessName ?? '',
      businessTypeId: data.businessTypeId ?? '',
      fullName: data.fullName ?? '',
      address: data.address ?? '',
      phone: data.phone ?? '',
      tierId,
    };

    setSubmitting(true);
    try {
      // full_name/phone belong on the user row, not the business — see
      // Task 4b. business creation also adds the caller as owner — see 4c.
      await updateMe({
        full_name: final.fullName,
        phone: final.phone,
      });
      const business = await createBusiness({
        name: final.businessName,
        business_type_id: final.businessTypeId || undefined,
        address: final.address,
        phone: final.phone,
      });
      // createBusinessRequest has no tier field — the tier picked in
      // step 3 is persisted via a follow-up POST /businesses/{id}/tier
      // call instead.
      if (final.tierId) {
        await setBusinessTier(business.id, final.tierId);
      }
      // Refetch /users/me so `user.businesses` picks up the new membership
      // before navigating into the app shell.
      await completeOnboarding();
      void navigate('/jobs');
    } catch {
      toast.error('We could not finish setting up your business. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-svh bg-background flex items-start justify-center p-4 py-10">
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <div className="inline-block rounded-xl bg-white px-4 py-2">
            <img src={vizitProBanner} alt="VizitPro" className="h-12 w-auto" />
          </div>
        </div>

        <StepIndicator current={step} labels={stepLabels} />

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xl">{titles[step].title}</CardTitle>
            <CardDescription>{titles[step].subtitle}</CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {step === 0 && (
              <StepBusiness defaultValues={data} onNext={handleBusiness} />
            )}
            {step === 1 && (
              <StepProfile defaultValues={data} onNext={handleProfile} onBack={() => setStep(0)} />
            )}
            {step === 2 && (
              <StepTier
                defaultTierId={data.tierId ?? ''}
                onNext={handleTier}
                onBack={() => setStep(1)}
                submitting={submitting}
              />
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-4">
          {user?.email}
        </p>
      </div>
    </div>
  );
}
